import React, { createContext, useContext, useEffect, useState } from 'react'
import { PortalTargetId } from './portal-target-id'

type PortalTargetCallback = {
  (arg0: HTMLElement): void
}

type PortalContextType = {
  setTarget: (id: PortalTargetId, node: HTMLElement) => void
  onTarget: (id: PortalTargetId, callback: PortalTargetCallback) => void
  offTarget: (id: PortalTargetId, callback: PortalTargetCallback) => void
}

const getDefaultContext = () => ({
  setTarget: () => {},
  onTarget: () => {},
  offTarget: () => {},
})

export const PortalContext = createContext<PortalContextType>(getDefaultContext())

export const PortalProvider = ({ children }: { children: React.ReactNode }) => {
  const targets = {} as {
    [key: string]: HTMLElement
  }
  const callbacks = {} as {
    [key: string]: PortalTargetCallback[]
  }

  const setTarget = (id: PortalTargetId, node: HTMLElement) => {
    targets[id] = node
    callbacks[id]?.forEach(callback => callback(node))
  }

  const onTarget = (id: PortalTargetId, callback: PortalTargetCallback) => {
    if (!callbacks[id]) {
      callbacks[id] = []
    }
    callbacks[id].push(callback)

    if (targets[id]) {
      callback(targets[id])
    }
  }

  const offTarget = (id: PortalTargetId, callback: PortalTargetCallback) => {
    if (!callbacks[id]) {
      return
    }
    callbacks[id] = callbacks[id].filter(c => c !== callback)
  }

  const state = {
    setTarget,
    onTarget,
    offTarget,
  }

  return <PortalContext.Provider value={state}>{children}</PortalContext.Provider>
}

export const usePortalTarget = (id?: PortalTargetId) => {
  const [targetNode, setTargetNode] = useState<HTMLElement | null>(null)
  const { onTarget, offTarget } = useContext(PortalContext)

  useEffect(() => {
    if (id) {
      const callback: PortalTargetCallback = node => {
        setTargetNode(node)
      }

      onTarget(id, callback)

      return () => {
        offTarget(id, callback)
      }
    }
  }, [id])

  return targetNode
}
